import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Controller for managing PDF header/footer rows with inline placeholder support
// Placeholders use $syntax (e.g., "Kunde: $customer.name")
export default class extends Controller {
  static targets = ["container", "output", "template", "empty", "placeholderHelp"]
  static values = {
    section: String, // 'header' or 'footer'
    maxRows: { type: Number, default: 8 },
  }

  connect() {
    this.initializeSortable()
    this.updateOutput()
    this.updateEmptyState()
  }

  initializeSortable() {
    if (this.hasContainerTarget) {
      new Sortable(this.containerTarget, {
        animation: 150,
        handle: ".drag-handle",
        ghostClass: "opacity-50",
        onEnd: () => {
          this.updateOutput()
          this.notifyChange()
        },
      })
    }
  }

  addRow(event) {
    event.preventDefault()

    const rows = this.containerTarget.querySelectorAll(".pdf-row")
    if (rows.length >= this.maxRowsValue) {
      alert(`Maximal ${this.maxRowsValue} Zeilen erlaubt`)
      return
    }

    const template = this.templateTarget.innerHTML
    const uniqueId = new Date().getTime()
    const newRow = template.replace(/NEW_INDEX/g, uniqueId)

    this.containerTarget.insertAdjacentHTML("beforeend", newRow)
    this.updateOutput()
    this.updateEmptyState()
    this.notifyChange()

    // Focus the first input in the new row
    const newRowElement = this.containerTarget.lastElementChild
    const firstInput = newRowElement.querySelector('input[type="text"]')
    if (firstInput) {
      firstInput.focus()
    }
  }

  removeRow(event) {
    event.preventDefault()
    const row = event.target.closest(".pdf-row")
    if (row) {
      row.remove()
      this.updateOutput()
      this.updateEmptyState()
      this.notifyChange()
    }
  }

  insertPlaceholder(event) {
    event.preventDefault()
    const placeholder = event.currentTarget.dataset.placeholder
    const side = event.currentTarget.dataset.side
    const row = event.target.closest(".pdf-row")

    if (!row || !placeholder) return

    const input = row.querySelector(`input[data-side="${side}"]`)
    if (input) {
      // Insert placeholder at cursor position or at end
      const cursorPos = input.selectionStart || input.value.length
      const textBefore = input.value.substring(0, cursorPos)
      const textAfter = input.value.substring(cursorPos)
      input.value = textBefore + "$" + placeholder + textAfter
      input.focus()

      // Move cursor after inserted placeholder
      const newPos = cursorPos + placeholder.length + 1
      input.setSelectionRange(newPos, newPos)

      this.updateOutput()
      this.notifyChange()
    }
  }

  togglePlaceholderHelp(event) {
    event.preventDefault()
    if (this.hasPlaceholderHelpTarget) {
      this.placeholderHelpTarget.classList.toggle("hidden")
    }
  }

  updateOutput() {
    const rows = this.containerTarget.querySelectorAll(".pdf-row")
    const data = []

    rows.forEach((row) => {
      const leftValue = row.querySelector('[data-side="left"]')?.value || ""
      const rightValue = row.querySelector('[data-side="right"]')?.value || ""
      const leftStyle = row.querySelector('[data-option="left_style"]')?.value || "normal"
      const leftLayout = row.querySelector('[data-option="left_layout"]')?.value || "inline"
      const rightStyle = row.querySelector('[data-option="right_style"]')?.value || "normal"
      const rightLayout = row.querySelector('[data-option="right_layout"]')?.value || "inline"

      data.push({
        left: leftValue,
        right: rightValue,
        left_style: leftStyle,
        left_layout: leftLayout,
        right_style: rightStyle,
        right_layout: rightLayout,
      })
    })

    if (this.hasOutputTarget) {
      this.outputTarget.value = JSON.stringify(data)
    }
  }

  // Rows added, removed or reordered and inserted placeholders change the form without an
  // input event; the settings card of the report type editor needs one to see the draft.
  notifyChange() {
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
  }

  updateEmptyState() {
    if (this.hasEmptyTarget) {
      const rows = this.containerTarget.querySelectorAll(".pdf-row")
      this.emptyTarget.classList.toggle("hidden", rows.length > 0)
    }
  }

  inputChanged() {
    this.updateOutput()
  }
}
